import { createRef, useMemo } from "react";

import { PalNumberFormat, PalDialog } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useTranslation } from "react-i18next";
import { MdAdd, MdDelete, MdEdit, MdPictureAsPdf } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import { Box, Typography } from "@mui/material";

import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRTable from "~components/Generic/PRTable";
import PalTooltip from "~components/mui/PalTooltip";
import { apiUrlBillingAndInvoicing, billSettingsCurrencyCodeMap } from "~constants";
import DateHelper from "~helpers/DateHelper";
import DialogHelper from "~helpers/DialogHelper";
import { deleteBill, getBillSettingList } from "~store/billingAndInvoicing/actions";
import { selectBillSettingList } from "~store/billingAndInvoicing/selectors";
import { selectCurrentProject } from "~store/user/selectors";

//make number formatter like this: 1,234.56 from float number without  Intl.NumberForma
const numberFormatter = (value, currencyCode) => {
  if (typeof value !== "number") return value;

  const comp = (
    <PalNumberFormat
      render
      NumericFormatProps={{
        decimalScale: 2,
        fixedDecimalScale: true,
        allowNegative: false,
        decimalSeparator: ".",
        thousandSeparator: ",",
        prefix: `${billSettingsCurrencyCodeMap[currencyCode]} `,
      }}
      value={value}
    />
  );
  return <Box sx={{ whiteSpace: "nowrap" }}>{comp}</Box>;
};

const InvoicesInfoModal = withCardon(
  function InvoicesInfoModalContent({ get, invoices }) {
    const { t } = useTranslation();

    const handleClose = () => {
      get(false)();
    };

    const invoiceColumns = useMemo(() => {
      return [
        {
          label: t("dashboard.invoices.invoiceNumber"),
          key: "invoice_number",
        },
        {
          label: t("dashboard.invoices.invoiceCode"),
          key: "invoice_code",
        },
        {
          label: t("dashboard.invoices.invoiceStatus"),
          key: "invoice_status",
        },
        {
          label: t("common.actions"),
          key: "actions",
          actions: true,
          render: (row) => (
            <div className="d-flex justify-content-center gap-1">
              <PRButton
                outline
                color="primary"
                icon={MdPictureAsPdf}
                size="sm"
                tooltipText={t("dashboard.draftCharges.viewPdf")}
                onClick={() => window.open(row.invoice_pdf, "_blank")}
              />
            </div>
          ),
        },
      ];
    }, [t]);

    return (
      <PalDialog open={true} size="md" title={t("dashboard.draftCharges.invoices")} onClose={handleClose}>
        <PalDialog.Title>{t("dashboard.draftCharges.invoices")}</PalDialog.Title>
        <PalDialog.Content dividers>
          {invoices.length > 0 ? (
            <PRTable columns={invoiceColumns} data={invoices} hidePagination={true} hideSearch={true} />
          ) : (
            <Typography>{t("dashboard.draftCharges.noInvoices")}</Typography>
          )}
        </PalDialog.Content>
      </PalDialog>
    );
  },
  { destroyOnHide: true }
);

const DraftChargesList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableRef = createRef();
  const currentProject = useSelector(selectCurrentProject);
  const billSettingList = useSelector(selectBillSettingList);

  useMemo(() => {
    dispatch(getBillSettingList(currentProject.id));
  }, [dispatch, currentProject.id]);

  const actions = useMemo(() => {
    return [
      {
        label: t("common.createNew"),
        icon: MdAdd,
        color: "success",
        link: "/billing-and-invoicing/draft-charges/form",
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    const handleClickDelete = (row) => async () => {
      const totalPrice = row?.items?.reduce((acc, item) => {
        return acc + (item.base_amount || 0) + (item.vat_amount || 0);
      }, 0);
      const matchedBillingSettingItem = billSettingList.find((item) => item.id === row.billing_settings);
      const currencyCode = billSettingsCurrencyCodeMap[matchedBillingSettingItem?.currency_code] || "";
      const priceFormattedAsTwoFloatingPoint = totalPrice.toFixed(2);

      const draftText = `${currencyCode} ${priceFormattedAsTwoFloatingPoint} - Due ${DateHelper.getDateTimeLocal(row.due_date).format("L")}`;
      if (await DialogHelper.showQuestionDeleteWithFormat(draftText)) {
        dispatch(deleteBill(currentProject.id, row.id)).then(() => {
          tableRef.current.refresh();
        });
      }
    };
    return [
      {
        label: t("dashboard.draftCharges.billingDueDate"),
        key: "due_date",
        render: (row) => {
          const isDueDatePassed = DateHelper.getDateTimeLocal(row.due_date).isBefore(DateHelper.getDateTime());
          // return DateHelper.getDateTimeLocal(row.due_date).format("L");
          return (
            <Box
              sx={{
                color: isDueDatePassed ? "error.main" : "text.primary",
              }}
            >
              {DateHelper.getDateTimeLocal(row.due_date).format("L")}
            </Box>
          );
        },
      },
      {
        label: t("dashboard.draftCharges.billingPeriodStart"),
        key: "billing_period_start",
        render: (row) => {
          return DateHelper.getDateTimeLocal(row.billing_period_start).format("L");
        },
      },
      {
        label: t("dashboard.draftCharges.billingPeriodEnd"),
        key: "billing_period_end",
        render: (row) => {
          return DateHelper.getDateTimeLocal(row.billing_period_end).format("L");
        },
      },
      {
        label: t("dashboard.draftCharges.totalBaseAmount"),
        key: "total_base_amount",
        render: (row) => {
          const matchedBillingSettingItem = billSettingList.find((item) => item.id === row.billing_settings);
          return <span>{numberFormatter(row.total_base_amount, matchedBillingSettingItem?.currency_code)}</span>;
        },
      },
      {
        label: t("dashboard.draftCharges.totalVatAmount"),
        key: "total_vat_amount",
        render: (row) => {
          const matchedBillingSettingItem = billSettingList.find((item) => item.id === row.billing_settings);
          return <span>{numberFormatter(row.total_vat_amount, matchedBillingSettingItem?.currency_code)}</span>;
        },
      },
      {
        label: t("component.billSettingsItem.totalPrice"),
        key: "total_price",
        render: (row) => {
          const matchedBillingSettingItem = billSettingList.find((item) => item.id === row.billing_settings);
          const totalPrice = row?.items.reduce((acc, item) => {
            return acc + (item.base_amount || 0) + (item.vat_amount || 0);
          }, 0);

          return (
            <span style={{ fontWeight: "bold" }}>
              {numberFormatter(totalPrice, matchedBillingSettingItem?.currency_code)}
            </span>
          );
        },
      },
      {
        label: t("dashboard.draftCharges.status"),
        key: "status",
        render: (row) => {
          return <span>{row.status}</span>;
        },
      },
      {
        label: t("dashboard.draftCharges.notes"),
        key: "notes",
        render: (row) => {
          const mergedNotes = row.notes?.length > 0 ? row.notes.join(", ") : "";
          const truncatedNote = mergedNotes?.length > 100 ? `${mergedNotes.slice(0, 100)}...` : mergedNotes;
          return (
            <Box display="flex" gap={1}>
              <PalTooltip title={mergedNotes}>{truncatedNote}</PalTooltip>
            </Box>
          );
        },
      },
      {
        label: t("common.actions"),
        key: "actions",
        actions: true,
        render: (row) => (
          <div className="d-flex justify-content-center gap-1">
            <PRButton
              outline
              color={row?.invoices?.length > 0 ? "primary" : "secondary"}
              icon={MdEdit}
              link={`/billing-and-invoicing/draft-charges/form/${row.id}`}
              size="sm"
              tooltipText={t("common.edit")}
            />
            <PRButton
              outline
              color={row?.invoices?.length > 0 ? "primary" : "secondary"}
              icon={MdPictureAsPdf}
              size="sm"
              tooltipText={t("dashboard.draftCharges.showInvoices")}
              onClick={() => InvoicesInfoModal.show({ invoices: row.invoices })}
            />
            <PRButton
              outline
              color="danger"
              icon={MdDelete}
              size="sm"
              tooltipText={t("common.delete")}
              onClick={handleClickDelete(row)}
            />
          </div>
        ),
      },
    ];
  }, [currentProject.id, dispatch, tableRef, t, billSettingList]);

  return (
    <PRContainer
      actions={actions}
      name={t("layout.sidebar.billingAndInvoicing")}
      parentName={t("layout.sidebar.draftCharges")}
    >
      <PRTable
        ref={tableRef}
        className="" //
        columns={columns}
        url={apiUrlBillingAndInvoicing.getBill.format(currentProject.id)}
      />
    </PRContainer>
  );
};

export default DraftChargesList;
